"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAccountStatistics, useAccountTier, type AccountTier } from "@/lib/api/accounts";
import { RemoteImage } from "@/components/ui/RemoteImage";
import { useShell } from "@/components/shell/ShellContext";
import { cn } from "@/lib/cn";
import { EASE } from "@/lib/motion";
import { Icon } from "@/components/ui/Icon";
import { Avatar, Button } from "@/components/ui/primitives";
import { ProfileAvatar } from "@/components/profile/ProfileAvatar";
import { PlayerStatsModal, type StatsTab } from "./PlayerStatsModal";
import { ProfileBanner } from "@/components/profile/ProfileBanner";

/* The player card from the kit: identity, lifetime stats, then actions.

   One card for every place a player's name appears — the live feeds, the
   chat — so a name means the same thing wherever it is clicked. Player
   statistics are public (`/accounts/{id}/statistics` needs no session), so
   these are the player's real numbers, fetched when the card opens rather
   than for every name on the screen. */

export type ProfileCardSide = "top" | "bottom";

const SIDE: Record<ProfileCardSide, string> = {
  top: "bottom-[calc(100%+6px)]",
  bottom: "top-[calc(100%+6px)]",
};

export function ProfileCard({
  name,
  accountId,
  onClose,
  side = "bottom",
  width = 295,
  className,
  anchor,
}: {
  name: string;
  accountId?: string;
  onClose: () => void;
  side?: ProfileCardSide;
  /* The kit draws it 295 wide; a narrower host, the chat rail, passes its own. */
  width?: number;
  className?: string;
  /* The name that opened it. Given, the card is drawn at the top of the page
     and placed against it, so no row, table or rail can paint over it or clip
     it: a theme that gives its rows their own layer (glass, blur, lift) would
     otherwise draw the rows after the name on top of the card. It closes if
     the page scrolls, where it would drift from the name. */
  anchor?: HTMLElement | null;
}) {
  const cardRef = useRef<HTMLSpanElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [position, setPosition] = useState<{ left: number; top?: number; bottom?: number } | null>(null);

  useLayoutEffect(() => {
    if (!anchor) return;

    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      setPosition(side === "top" ? { left, bottom: window.innerHeight - rect.top + 6 } : { left, top: rect.bottom + 6 });
    };
    /* Only a scroll that moves the name away closes it: the page's own, or a
       scroller the name sits inside. A carousel or ticker scrolling itself
       elsewhere on the page, the card, or the stats window it opened, is
       not the page moving away. */
    const onScroll = (event: Event) => {
      const target = event.target;
      const movesName = target === document || (target instanceof Node && target.contains(anchor));
      if (movesName) onCloseRef.current();
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [anchor, side, width]);

  const { openWallet } = useShell();
  const statistics = useAccountStatistics(accountId, true);
  const tier = useAccountTier(accountId, statistics.data);
  /* Each total opens the advanced stats at the list behind it, as the
     whitelabel's card does. Wagered is made of bets, so it opens those. */
  const [statsTab, setStatsTab] = useState<StatsTab | null>(null);
  const joined = statistics.data
    ? new Date(statistics.data.createdAt).toLocaleDateString("en-GB", { month: "long", year: "numeric" })
    : null;
  const flipped = side === "top";

  if (anchor && (!position || typeof document === "undefined")) return null;

  const card = (
    <motion.span
      ref={cardRef}
      initial={{ opacity: 0, y: flipped ? -4 : 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: flipped ? -4 : 4 }}
      transition={{ duration: 0.2, ease: EASE }}
      style={anchor && position ? { width, ...position } : { width }}
      className={cn(
        "modal-shadow overflow-hidden rounded-lg border border-border bg-elevation-1",
        anchor ? "fixed z-[70] block" : cn("absolute left-0 z-50", SIDE[side]),
        className,
      )}
    >
      <ProfileBanner accountId={accountId}>
      <span className="flex h-[72px] items-center gap-[12px] px-[16px]">
        <ProfileAvatar accountId={accountId} name={name} size={40} glow />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[14px] font-bold">{name}</span>
          <TierLine tier={tier} joined={joined} />
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="grid h-[24px] w-[24px] shrink-0 place-items-center text-fg transition-colors hover:text-fg-muted"
        >
          <Icon name="close" size={18} />
        </button>
      </span>
      </ProfileBanner>
      <span className="grid grid-cols-3 border-y border-border">
        <Stat label="Wagered" value={statistics.data?.wagered ?? "—"} onClick={accountId ? () => setStatsTab("bets") : undefined} />
        <Stat label="Bets" value={statistics.data ? String(statistics.data.bets) : "—"} onClick={accountId ? () => setStatsTab("bets") : undefined} />
        <Stat
          label="Transactions"
          value={statistics.data ? String(statistics.data.transactions) : "—"}
          onClick={accountId ? () => setStatsTab("transactions") : undefined}
        />
      </span>
      <span className="flex gap-[8px] p-[16px]">
        <Button variant="primary" size="sm" icon="coins" className="flex-1" onClick={() => openWallet("Tip")}>
          Tip
        </Button>
        <Link
          href="/account"
          className="flex h-[30px] flex-1 items-center justify-center gap-[8px] rounded-md border border-brand px-[8px] text-[12px] font-bold leading-[1.6] text-fg transition-colors hover:bg-brand-bg-soft"
        >
          <Icon name="eye-off" size={12} />
          Ignore
        </Link>
      </span>
      {accountId && (
        <PlayerStatsModal
          accountId={statsTab ? accountId : null}
          name={name}
          initialTab={statsTab ?? "overview"}
          onClose={() => setStatsTab(null)}
        />
      )}
    </motion.span>
  );

  return anchor ? createPortal(card, document.body) : card;
}

/* The line under the name, as the kit's profile-hover draws it: the tier's
   icon and name, a dot, the join date. Without a known tier it is the join
   date alone — the platform tells us no other player's tier yet, and a
   guessed crest would be worse than none. Exported for the stats modal's
   overview, which draws the same line. */
export function TierLine({ tier, joined }: { tier: AccountTier | null; joined: string | null }) {
  return (
    <span className="flex min-w-0 items-center gap-[6px] text-[12px] text-fg-subtle">
      {tier && (
        <>
          <span className="relative h-[14px] w-[14px] shrink-0 overflow-hidden rounded-full bg-elevation-3">
            {tier.imageUrl && <RemoteImage src={tier.imageUrl} alt={tier.name} width={14} height={14} />}
          </span>
          <span className="truncate font-bold text-fg-muted">{tier.name}</span>
          {joined && <span aria-hidden>·</span>}
        </>
      )}
      <span className="truncate">{joined ? `Joined ${joined}` : tier ? "" : " "}</span>
    </span>
  );
}

/* A total. With something to open it is a button and looks like one: the
   pointer, a hover, and a title saying where it goes. */
function Stat({ label, value, onClick }: { label: string; value: string; onClick?: () => void }) {
  const body = (
    <>
      <span className="text-[9px] font-bold uppercase tracking-wide text-fg-subtle">{label}</span>
      <span className="text-[12px] font-bold">{value}</span>
    </>
  );

  if (!onClick) {
    return (
      <span className="flex flex-col items-center gap-0.5 border-r border-border py-2 last:border-0">{body}</span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={`See ${label.toLowerCase()} in detail`}
      className="flex cursor-pointer flex-col items-center gap-0.5 border-r border-border py-2 transition-colors last:border-0 hover:bg-elevation-2"
    >
      {body}
    </button>
  );
}
